"use client";

import * as React from "react";

import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/ui/section-heading";
import { AppSwiper, SwiperSlide } from "@/components/ui/swiper";
import Link from "next/link";
import { useProductReviews } from "@/hooks/productReview";
import type { StrapiCollectionResponse } from "@/lib/strapi";
import type { ProductReviewAttributes } from "@/services/productReview";

export function TestimonialsSection(props?: { initialData?: StrapiCollectionResponse<ProductReviewAttributes> | null }) {
  const { reviews, isLoading } = useProductReviews({ pageSize: 25, initialData: props?.initialData });

  const testimonials = React.useMemo(() => {
    if (!reviews.length) {
      return [
        {
          id: "testimonial-1",
          ratingText: "5.0 ★",
          name: "Budi Alonso",
          meta: "Jakarta | 12 Nov 2025",
          quote:
            "Romano Classic has a clean and masculine scent that lasts surprisingly long for a cologne at this price. Fresh, confident, and perfect for daily use. Not overpowering, just right!",
        },
        {
          id: "testimonial-2",
          ratingText: "5.0 ★",
          name: "N. Salma",
          meta: "Bandung | 03 Oct 2025",
          quote:
            "Sillage-nya pas, aromanya premium. Setelah beberapa jam masih terasa hangat dan nyaman, cocok buat aktivitas seharian.",
        },
        {
          id: "testimonial-3",
          ratingText: "4.8 ★",
          name: "D. Farhan",
          meta: "Surabaya | 28 Sep 2025",
          quote:
            "Packaging-nya rapi dan elegan. Wanginya juga versatile, bisa dipakai formal ataupun casual.",
        },
        {
          id: "testimonial-4",
          ratingText: "5.0 ★",
          name: "R. Aditya",
          meta: "Medan | 14 Aug 2025",
          quote:
            "Kesan pertama langsung mewah. Aromanya bersih dan maskulin, tapi tetap nyaman dipakai harian.",
        },
        {
          id: "testimonial-5",
          ratingText: "4.7 ★",
          name: "S. Nadia",
          meta: "Semarang | 01 Jul 2025",
          quote:
            "Tahan lama dan projeknya pas. Cocok buat kantor, tapi masih oke juga buat hangout.",
        },
      ];
    }

    return reviews.map((entity) => {
      const attributes = entity.attributes as {
        username?: string | null;
        platform?: string | null;
        rating?: number | null;
        review?: string | null;
        createdAt?: string | null;
        updatedAt?: string | null;
        publishedAt?: string | null;
        date?: string | null;
        LabelSelect?: string | null;
      };

      const rating = typeof attributes.rating === "number" ? attributes.rating : null;
      const ratingText = `${(rating ?? 5).toFixed(1)} ★`;
      const name = attributes.username?.trim() || "Anonymous";

      const dateString = attributes.date ?? attributes.publishedAt ?? attributes.updatedAt ?? attributes.createdAt;
      const metaParts = [attributes.platform ?? attributes.LabelSelect].filter(Boolean) as string[];
      if (dateString) {
        const dt = new Date(dateString);
        if (!Number.isNaN(dt.getTime())) {
          metaParts.push(
            new Intl.DateTimeFormat("id-ID", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }).format(dt),
          );
        }
      }

      const meta = metaParts.join(" | ") || "Product Review";
      const quote = (attributes.review ?? "").trim() || "Great product experience.";

      return {
        id: String(entity.id),
        ratingText,
        name,
        meta,
        quote,
      };
    });
  }, [reviews]);

  return (
    <section
      id="about"
      className="mx-auto w-full px-4 sm:px-6 lg:px-10"
    >
      <SectionHeading eyebrow="Popular Products" title="What They Say About Us" />

      {isLoading && reviews.length === 0 ? (
        <div className="mx-auto mt-10 max-w-5xl animate-pulse">
          <div className="mx-auto flex h-[clamp(260px,70vw,344px)] max-w-3xl flex-col items-center justify-between rounded-3xl bg-muted/30 px-8 py-10 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="h-4 w-16 rounded-lg bg-muted" />
              <div className="h-4 w-40 rounded-lg bg-muted" />
            </div>

            <div className="w-full max-w-2xl space-y-3">
              <div className="h-4 w-full rounded-lg bg-muted" />
              <div className="h-4 w-[92%] rounded-lg bg-muted" />
              <div className="h-4 w-[80%] rounded-lg bg-muted" />
            </div>

            <div className="h-10 w-32 rounded-full bg-muted" />
          </div>
        </div>
      ) : (
        <AppSwiper
          className="mx-auto mt-10 max-w-5xl overflow-visible"
          swiperClassName="pb-10 sm:pb-6"
          slidesPerView={1}
          spaceBetween={24}
          watchOverflow
          autoHeight
          navigationButtonClassName="h-10 w-10 rounded-full bg-[#2F2F2F] text-white shadow-none hover:bg-[#1F1F1F]"
          prevButtonClassName="left-0 -translate-x-1/2"
          nextButtonClassName="right-0 translate-x-1/2"
        >
          {testimonials.map((testimonial) => (
            <SwiperSlide key={testimonial.id} className="h-auto">
              <div className="mx-auto flex h-auto min-h-[clamp(260px,70vw,344px)] max-w-3xl flex-col items-center justify-between gap-8 py-8 text-center sm:h-[clamp(260px,70vw,344px)] sm:gap-0 sm:py-0">
                <div className="flex flex-col items-center">
                  <p className="text-[clamp(13px,1.1vw,16px)] font-medium tracking-tight text-foreground">
                    {testimonial.ratingText}
                  </p>
                  <p className="text-[clamp(13px,1.1vw,16px)] font-medium text-foreground">
                    {testimonial.name}
                  </p>
                  {/* <p className="text-[clamp(12px,1vw,14px)] tracking-wide text-[#868686]">
                    {testimonial.meta}
                  </p> */}
                </div>

                <div className="relative px-10 pb-6 sm:px-12 sm:pb-0">
                  <span
                    aria-hidden="true"
                    className="absolute h-8 left-0 top-0 text-5xl leading-none text-[#CCCCCC] sm:text-7xl"
                  >
                    “
                  </span>
                  <p className="text-[clamp(18px,2vw,26px)] font-normal leading-[1.6] tracking-tight text-foreground">
                    {testimonial.quote}
                  </p>
                  <span
                    aria-hidden="true"
                    className="absolute right-0 bottom-0 h-8 text-5xl leading-none text-[#CCCCCC] sm:text-7xl"
                  >
                    ”
                  </span>
                </div>

                <Button
                  asChild
                  className="rounded-full bg-foreground px-8 text-[clamp(11px,0.95vw,13px)] font-semibold tracking-[0.18em] text-background hover:bg-foreground/90"
                >
                  <Link href="/buy-now">BUY NOW</Link>
                </Button>
              </div>
            </SwiperSlide>
          ))}
        </AppSwiper>
      )}
    </section>
  );
}
